import { FugFile } from '@campfire/file-upload-gallery/lib';
import { Recurrence } from '../../../../common/recurrence/__generated__/Recurrence';
import { useEndpointFetch } from '../../../../global/network/useEndpointFetch';
import { ActivitiesConsoleExploreActivitySession } from '../activities-list/__generated__/ActivitiesConsoleExploreActivitySession';
import { ActivitiesConsoleCardExploreActivity } from '../activities-list/__generated__/ActivitiesConsoleCardExploreActivity';
import { ActivitiesConsoleExploreActivityLocation } from '../activities-list/__generated__/ActivitiesConsoleExploreActivityLocation';

export const useSuspendActivityFetch = () => {
  return useEndpointFetch<{ activityId: string }>('/vm/activity/suspend');
};

export const useUnsuspendActivityFetch = () => {
  return useEndpointFetch<{ activityId: string }>('/vm/activity/unsuspend');
};

export const useCloseActivityFetch = () => {
  return useEndpointFetch<{ activityId: string; dateEnded: string; activityClosureReasonId: string }>(
    '/vm/activity/close'
  );
};

export const useUncloseActivityFetch = () => {
  return useEndpointFetch<{ closedActivityId: string }>('/vm/activity/unclose');
};

export const useCancelActivityFetch = () => {
  return useEndpointFetch<
    {
      activityId: string;
      activityDate: string;
      activityCancellationReasonId: string;
      description: string;
      addedAttachments?: FugFile[];
      attachmentCreationTokens?: string[];
    },
    { cancelledActivityId: string }
  >('/vm/activity/cancel', { formData: true });
};

export const useUncancelActivityFetch = () => {
  return useEndpointFetch<{ cancelledActivityId: string }>('/vm/activity/uncancel');
};

export const useSaveActivityTeamFetch = () => {
  return useEndpointFetch<{ activityId: string; teamMembers: { volunteerId: string; position: string }[] }>(
    '/vm/activity/team/save'
  );
};

export const useApproveIncomingVolunteerFetch = () => {
  return useEndpointFetch<{ activityApplicationId: string }, { activityEnrolmentId: string }>(
    '/vm/activity/incoming/approve'
  );
};

export const useApproveBulkIncomingVolunteersFetch = () => {
  return useEndpointFetch<{ activityApplicationIds: string[] }, { activityEnrolmentIds: string[] }>(
    '/vm/activity/incoming/approve-bulk'
  );
};

export const useWaitlistIncomingVolunteerFetch = () => {
  return useEndpointFetch<{ activityApplicationId: string }, { activityWaitlistingId: string }>(
    '/vm/activity/incoming/waitlist'
  );
};

export const useWaitlistBulkIncomingVolunteersFetch = () => {
  return useEndpointFetch<{ activityApplicationIds: string[] }, { activityWaitlistingIds: string[] }>(
    '/vm/activity/incoming/waitlist-bulk'
  );
};

export const useRejectIncomingVolunteerFetch = () => {
  return useEndpointFetch<{ activityApplicationId: string }>('/vm/activity/incoming/reject');
};

export const useEnrolWaitlistingFetch = () => {
  return useEndpointFetch<{ activityWaitlistingId: string }, { activityEnrolmentId: string }>(
    '/vm/activity/waitlist/enrol'
  );
};

export const useUnenrolWaitlist = () => {
  return useEndpointFetch<{ activityWaitlistingId: string; removalReason?: string }, { activityWaitlistingId: string }>(
    '/vm/activity/waitlist/remove'
  );
};

export const useEnrolBulkWaitlist = () => {
  return useEndpointFetch<{ activityWaitlistingIds: string[] }, { activityWaitlistingIds: string[] }>(
    '/vm/activity/waitlist/approve-bulk'
  );
};

export const useUnenrolBulkWaitlist = () => {
  return useEndpointFetch<
    { activityWaitlistingIds: string[]; removalReason?: string },
    { activityWaitlistingIds: string[] }
  >('/vm/activity/waitlist/remove-bulk');
};

export const useAddWaitlistingFetch = () => {
  return useEndpointFetch<{ activityId: string }, { activityWaitlistingId: string }>('/vm/activity/waitlist/add');
};

export const useRemoveWaitlistingFetch = () => {
  return useEndpointFetch<{ activityWaitlistingId: string }>('/vm/activity/waitlist/remove');
};

export const useRejectActivityApplicationFetch = () => {
  return useEndpointFetch<{ activityApplicationId: string }>('/vm/activity/incoming/reject');
};

export const useRejectBulkActivityApplicationFetch = () => {
  return useEndpointFetch<{ activityApplicationIds: string[] }>('/vm/activity/incoming/reject-bulk');
};

export const useApproveActivityApplicationFetch = () => {
  return useEndpointFetch<{ activityApplicationId: string }>('/vm/activity/incoming/approve');
};

export const useApproveBulkActivityApplicationFetch = () => {
  return useEndpointFetch<{ activityApplicationIds: string[] }>('/vm/activity/incoming/approve-bulk');
};
interface PlacesAddress {
  description: string;
  formatted: string;
  placesId?: string;
  latitude: number;
  longitude: number;
}

export interface SaveActivityFetchActivityLocationVariables {
  activityLocationId?: string;
  placesAddress: PlacesAddress;
  timeZone: string;
  comments: string;
}

export interface SaveActivityFetchSessionVariables {
  sessionId?: string;
  name: string;
  description: string;
  minVolunteers?: number | null;
  maxVolunteers?: number | null;
  activityLocation?: SaveActivityFetchActivityLocationVariables | null;
  startTime: string | null;
  endTime: string | null;
  reportTypeId: string | null;
  isFlexible?: boolean;
}

export interface SaveActivityFetchVariables {
  activityId?: string;
  isRestrictedActivity: boolean;
  isHidden: boolean;
  maxTeam: number | null;
  hasOpenRoster: boolean;
  allowCICO: boolean | null;
  programId: string;
  name: string;
  description: string;
  startDate: string;
  endDate?: string;
  enrollable?: boolean;
  activityLocation?: SaveActivityFetchActivityLocationVariables | null;
  remoteLocation?: {
    details?: string;
  };
  recurrences?: Omit<Recurrence, 'humanReadable'>[];
  sessions: SaveActivityFetchSessionVariables[];
  addedAttachments?: FugFile[];
  attachmentCreationTokens?: string[];
  removedAttachmentIds?: string[];
  activityTagIds?: string[];
  priority?: boolean;
}

export const useSaveActivityFetch = () => {
  return useEndpointFetch<SaveActivityFetchVariables, { activityId: string }>('/vm/activity/save', { formData: true });
};

export const useSetPriorityActivityFetch = () => {
  return useEndpointFetch<{ activityId: string; programId: string; priority: boolean }>('/vm/activity/priority', {
    formData: true,
  });
};

export const useSaveUnavailabilityFetch = () => {
  return useEndpointFetch<{ activityEnrolmentId: string; activityDate: string }>(
    '/vm/activity/enrolment/unavailability/save'
  );
};

export const useWithdrawUnavailabilityFetch = () => {
  return useEndpointFetch<{ activityUnavailabilityId: string }>('/vm/activity/enrolment/unavailability/withdraw');
};

export const useSaveDraftRosterFetch = () => {
  return useEndpointFetch<
    {
      activityId: string;
      activityDate: string;
      draftRosterings: {
        volunteerId: string;
        sessionIds: string[];
      }[];
      immediatePublish?: boolean;
      rosterTemplateId?: string;
    },
    { draftRosterId: string }
  >('/vm/activity/roster/save-draft');
};

export const useCancelSessionReportFetch = () => {
  return useEndpointFetch<{ sessionId: string; activityDate: string }, { activityReportId: string }>(
    '/vm/session/report/cancel'
  );
};

interface BaseFieldValue {
  fieldId: string;
}

interface BooleanFieldValue extends BaseFieldValue {
  __typename: 'VOLUNTEER_BooleanFieldValue';
  boolValue: boolean;
}

interface CheckboxFieldValue extends BaseFieldValue {
  __typename: 'VOLUNTEER_CheckboxFieldValue';
  checked: boolean;
}

interface DropdownFieldValue extends BaseFieldValue {
  __typename: 'VOLUNTEER_DropdownFieldValue';
  dropdownFieldOptionIds: string[];
}

interface NumericFieldValue extends BaseFieldValue {
  __typename: 'VOLUNTEER_NumericFieldValue';
  numericValue: number;
}

interface RatingFieldValue extends BaseFieldValue {
  __typename: 'VOLUNTEER_RatingFieldValue';
  ratingValue: number;
}

interface TextFieldValue extends BaseFieldValue {
  __typename: 'VOLUNTEER_TextFieldValue';
  textValue: string;
}

interface TimeFieldValue extends BaseFieldValue {
  __typename: 'VOLUNTEER_TimeFieldValue';
  timeValue: string;
}

export type SaveSessionEndpointFieldValueType =
  | BooleanFieldValue
  | CheckboxFieldValue
  | DropdownFieldValue
  | NumericFieldValue
  | RatingFieldValue
  | TextFieldValue
  | TimeFieldValue;

export interface SaveSessionEndpointDataType {
  sessionId: string;
  activityDate: string;
  startTime: string;
  endTime: string;
  volunteerAttendances: Array<string>;
  otherAttendances: Array<string>;
  comments: string;
  fieldValues: SaveSessionEndpointFieldValueType[];
}

export const useSaveSessionReportFetch = () => {
  return useEndpointFetch<SaveSessionEndpointDataType, { activityReportId: string; sessionReportId: string }>(
    '/vm/session/report/save'
  );
};

function parseActivityLocation(
  activityLocation: ActivitiesConsoleExploreActivityLocation
): SaveActivityFetchActivityLocationVariables {
  return {
    ...activityLocation,
    comments: activityLocation.comments ?? '',
    placesAddress: {
      ...activityLocation.placesAddress,
      placesId: activityLocation.placesAddress.placesId ?? undefined,
      latitude: activityLocation.placesAddress.latLong.lat,
      longitude: activityLocation.placesAddress.latLong.long,
    },
  };
}

function parseSession(session: ActivitiesConsoleExploreActivitySession): SaveActivityFetchSessionVariables {
  return {
    ...session,
    reportTypeId: session.reportType?.reportTypeId
      ? session.reportType?.reportTypeId
      : session.autoReport
      ? NO_REPORT_IMPACT_TRACKED
      : NO_REPORT_NO_IMPACT,
    activityLocation: session.activityLocation ? parseActivityLocation(session.activityLocation) : null,
  };
}

export function parseActivity(activity: ActivitiesConsoleCardExploreActivity): SaveActivityFetchVariables {
  return {
    activityId: activity.activityId,
    isRestrictedActivity: activity.isRestrictedActivity,
    isHidden: activity.isHidden,
    maxTeam: activity.maxTeam,
    hasOpenRoster: activity.hasOpenRoster,
    allowCICO: activity.allowCICO,
    programId: activity.program.programId,
    name: activity.name,
    description: JSON.stringify(activity.description),
    startDate: activity.startDate,
    enrollable: activity.isEnrollable,
    activityLocation: activity.activityLocation ? parseActivityLocation(activity.activityLocation) : null,
    remoteLocation: {
      details: activity?.activityRemoteLocation?.details ?? undefined,
    },
    recurrences: activity.__typename === 'VOLUNTEER_RecurringActivityType' ? activity.schedule.recurrences : [],
    sessions: activity.sessions.map(parseSession),
    activityTagIds: activity.activityTags.map((tag) =>
      tag &&
      activity.activityTagged.some(
        (activityTaggedItem) => activityTaggedItem.activityTag.activityTagId === tag.activityTagId
      )
        ? tag.activityTagId
        : ''
    ),
  };
}

export const NO_REPORT_NO_IMPACT = 'no-report-no-impact';
export const NO_REPORT_IMPACT_TRACKED = 'no-report-impact-tracked';

export const NO_REPORT_TYPES = [
  {
    reportTypeId: NO_REPORT_NO_IMPACT,
    name: 'No Report Required (No Impact)',
    description: `Volunteers will not be required or able to fill in a report for this session, and the report will be treated as ‘completed’. `,
  },
  {
    reportTypeId: NO_REPORT_IMPACT_TRACKED,
    name: 'No Report Required (Impact Tracked)',
    description:
      'At the conclusion of the session, the report will be auto-generated and completed, pre-filling rostered volunteers and session times on the report. If additional information is required, like a journal entry, volunteers will still be able to edit the report.',
  },
];
